/**
 * This file is to simulate the old school window.alert but wrap everything
 * within a promise and nice UI. To use this promise,
 * simply: const result = await Alert({body: 'Are you sure?'});
 */
import React, { ReactElement, useCallback, useState } from 'react';
import { createRoot } from 'react-dom/client';
import Button from '@atlaskit/button/standard-button';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalTransition,
} from '@atlaskit/modal-dialog';

let alertPromiseResolve: Function;
let alertModal: HTMLDivElement | null;

type AlertProps = {
  title?: string;
  body: string | ReactElement;
};

const AlertModal: React.FC<AlertProps> = ({ title = 'Alert', body }) => {
  const [isOpen, setIsOpen] = useState(true);
  const handleOKClick = useCallback(() => {
    setIsOpen(false);
    alertPromiseResolve(true);
    if (alertModal) {
      alertModal.remove();
      alertModal = null;
    }
  }, []);

  if (!isOpen) return <></>;

  return (
    <div>
      <ModalTransition>
        <Modal onClose={handleOKClick}>
          <ModalHeader>
            <ModalTitle>{title}</ModalTitle>
          </ModalHeader>
          <ModalBody>{body}</ModalBody>
          <ModalFooter>
            <Button appearance="primary" onClick={handleOKClick} autoFocus>
              OK
            </Button>
          </ModalFooter>
        </Modal>
      </ModalTransition>
    </div>
  );
};

export const Alert = async ({ title, body }: AlertProps): Promise<boolean> => {
  if (alertModal) {
    alertModal.remove();
  } else {
    alertModal = document.createElement('div');
  }
  if (alertPromiseResolve) alertPromiseResolve(false);

  document.body.appendChild(alertModal);

  const alertModalRoot = createRoot(alertModal!);
  alertModalRoot.render(<AlertModal title={title} body={body} />);

  return new Promise((res: Function) => {
    alertPromiseResolve = res;
  });
};
